import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { CreateApiKey, DeleteApiKey, FetchApiKeys } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import {
  CreateCustomerApiKeyParams,
  CreateCustomerApiKeyResponse,
  CustomerApiKey,
  DeleteCustomerApiKeyParams,
} from 'src/interfaces/apiKey'

const normalizeApiKeysResponse = (payload: unknown): Array<CustomerApiKey> => {
  if (Array.isArray(payload)) {
    return payload as Array<CustomerApiKey>
  }

  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return (payload as { data: Array<CustomerApiKey> }).data
  }

  return []
}

function* onFetchApiKeys(action: AsyncAction<void, Array<CustomerApiKey>>) {
  try {
    const { payload }: { payload: unknown } = yield call(callSecureApi, Endpoints.FetchApiKeys, {
      apiService: ApiService.Paraworks,
    })

    const keys = normalizeApiKeysResponse(payload)

    yield put(FetchApiKeys.success(keys))

    if (action.next) {
      action.next(null, keys)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch API keys'

    yield put(FetchApiKeys.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCreateApiKey(
  action: AsyncAction<CreateCustomerApiKeyParams, CreateCustomerApiKeyResponse>,
) {
  try {
    const { name } = action.payload

    const { payload: rawPayload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.CreateApiKey,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: { name },
      },
    )

    const body = rawPayload as CreateCustomerApiKeyResponse | { data: CreateCustomerApiKeyResponse }
    const created =
      body && typeof body === 'object' && 'data' in body
        ? (body as { data: CreateCustomerApiKeyResponse }).data
        : (body as CreateCustomerApiKeyResponse)

    yield put(CreateApiKey.success())
    yield put(FetchApiKeys.request())

    if (action.next) {
      action.next(null, created)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create API key'

    yield put(CreateApiKey.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onDeleteApiKey(action: AsyncAction<DeleteCustomerApiKeyParams, void>) {
  try {
    const { keyId } = action.payload

    yield call(callSecureApi, Endpoints.DeleteApiKey, {
      apiService: ApiService.Paraworks,
      method: 'DELETE',
      params: [{ field: ':keyId', value: keyId }],
    })

    yield put(DeleteApiKey.success())
    yield put(FetchApiKeys.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete API key'

    yield put(DeleteApiKey.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* apiKeySaga() {
  yield takeEvery(FetchApiKeys.type.REQUEST, onFetchApiKeys)
  yield takeEvery(CreateApiKey.type.REQUEST, onCreateApiKey)
  yield takeEvery(DeleteApiKey.type.REQUEST, onDeleteApiKey)
}
